import {
  LayoutGrid,
  ShoppingCart,
  Bell,
  MessageCircle,
  User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/css";
import { Badge } from "@/shared/ui/kit/badge";

import type { NavItemType } from "../model";

type NavBarItemProps = {
  type: NavItemType;
  labelKey: string;
  isActive: boolean;
  badgeCount?: number;
  onClick: () => void;
};

const iconMap: Record<NavItemType, LucideIcon> = {
  catalog: LayoutGrid,
  cart: ShoppingCart,
  notifications: Bell,
  tickets: MessageCircle,
  user: User,
};

export const NavBarItem = ({
  type,
  labelKey,
  isActive,
  badgeCount = 0,
  onClick,
}: NavBarItemProps) => {
  const { t } = useTranslation();
  const Icon = iconMap[type];
  const label = t(labelKey);

  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center justify-center gap-1.5 px-1 py-1 transition-all duration-200 active:scale-95"
      aria-label={label}
    >
      <div
        className={cn(
          "relative flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-200",
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground group-hover:bg-accent group-hover:text-accent-foreground",
        )}
      >
        <Icon className="w-5 h-5" />
        {badgeCount > 0 && (
          <Badge className="absolute -top-0.5 -right-0.5 h-3.5 min-w-3.5 px-0.5 text-[9px] rounded-full flex items-center justify-center">
            {badgeCount > 99 ? "99+" : badgeCount}
          </Badge>
        )}
      </div>
      <span
        className={cn(
          "text-[10px] font-medium leading-none transition-colors duration-200",
          isActive ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
    </button>
  );
};
